import { CoreTypes, ErrorCodes, type ConnectorEnd, type Id, type ObjectRecord, type Style } from '@vp/shared-types';
import { CanvasDocument, commands, CoordinateLimitError, keysBetween, makeObject } from '@vp/core';
import { RpcError } from './rpc';

/** An object of a new canvas (API 2.6), its type's defaults and style already applied. */
export interface CanvasBuildObject {
  ref?: string;
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  rotation?: number;
  locked?: boolean;
  props: Record<string, unknown>;
  style?: Record<string, unknown>;
  name?: string;
  formatVersion?: number;
  group?: string;
  frame?: string;
  image?: string;
  link?: string;
}

const CORE_TYPES: ReadonlySet<string> = new Set(Object.values(CoreTypes));
const LINK_PROTOCOLS = new Set(['https:', 'http:', 'mailto:']);

function invalid(message: string): RpcError {
  return new RpcError(ErrorCodes.invalidParams, message);
}

function webLink(raw: string): string {
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    throw invalid(`link ${raw.slice(0, 80)} is not an address`);
  }
  if (!LINK_PROTOCOLS.has(u.protocol)) throw invalid(`link ${raw.slice(0, 80)}: only https, http and mailto`);
  return u.href;
}

function finitePoint(p: unknown): p is { x: number; y: number } {
  const q = p as { x?: unknown; y?: unknown } | null;
  return !!q && typeof q.x === 'number' && typeof q.y === 'number' && Number.isFinite(q.x) && Number.isFinite(q.y);
}

/**
 * The records of a new canvas (API 2.6 `canvases.create`) built without an editor: layer keys by list
 * order, groups with boxes of their members, frames holding their contents, arrows glued to objects
 * of the list, pictures put in place. Throws `RpcError` for a spec that cannot be built.
 */
export function buildCanvas(canvasId: Id, objects: CanvasBuildObject[], images: ReadonlyMap<string, string>): ObjectRecord[] {
  const refs = new Map<string, number>();
  objects.forEach((o, i) => {
    if (o.type.startsWith('core:') && !CORE_TYPES.has(o.type)) throw invalid(`unknown type ${o.type}`);
    if (!o.ref) return;
    if (refs.has(o.ref)) throw invalid(`ref ${o.ref} is used twice`);
    refs.set(o.ref, i);
  });
  const indexOf = (ref: string, what: string, type: string): number => {
    const i = refs.get(ref);
    if (i === undefined) throw new RpcError(ErrorCodes.notFound, `${what} ${ref} is not in the list`);
    if (objects[i].type !== type) throw invalid(`${what} ${ref} is not a ${type}`);
    return i;
  };

  const ids = objects.map(() => '');
  const keys = keysBetween(null, null, objects.length);
  const records: ObjectRecord[] = objects.map((o, i) => {
    const props = { ...o.props };
    if (o.image !== undefined) {
      if (o.type !== CoreTypes.image) throw invalid(`image ${o.image} is for core:image objects`);
      const hash = images.get(o.image);
      if (!hash) throw new RpcError(ErrorCodes.notFound, `image ${o.image} is not in the spec`);
      props['attachment'] = hash;
    }
    const rec = makeObject(canvasId, {
      type: o.type,
      x: o.x,
      y: o.y,
      w: o.w,
      h: o.h,
      z: keys[i],
      rotation: o.rotation ?? 0,
      props,
      style: o.style as Partial<Style> | undefined,
      formatVersion: o.formatVersion,
      ...(o.name !== undefined ? { name: o.name } : {}),
    });
    if (o.locked) rec.locked = true;
    if (o.link !== undefined) rec.link = { kind: 'url', target: webLink(o.link) };
    ids[i] = rec.id;
    return rec;
  });

  // groups: members point at their group; a group may sit in another one, never in itself
  const parentOf = new Map<number, number>();
  objects.forEach((o, i) => {
    if (o.group === undefined) return;
    const g = indexOf(o.group, 'group', CoreTypes.group);
    if (g === i) throw invalid(`group ${o.group} cannot hold itself`);
    parentOf.set(i, g);
    records[i].parentId = ids[g];
  });
  for (const start of parentOf.keys()) {
    const seen = new Set<number>([start]);
    for (let cur = parentOf.get(start); cur !== undefined; cur = parentOf.get(cur)) {
      if (seen.has(cur)) throw invalid('groups hold each other in a circle');
      seen.add(cur);
    }
  }
  const rootOf = (i: number): number => {
    let cur = i;
    while (parentOf.has(cur)) cur = parentOf.get(cur)!;
    return cur;
  };

  // frames hold the outermost group of an object, and lie under what they hold
  objects.forEach((o, i) => {
    if (o.frame === undefined) return;
    const f = indexOf(o.frame, 'frame', CoreTypes.frame);
    if (f >= i) throw invalid(`frame ${o.frame} must come before the objects it holds`);
    const root = rootOf(i);
    if (root === f || rootOf(f) === root) throw invalid(`frame ${o.frame} cannot hold its own group`);
    if (!records[root].frameId) records[root].frameId = ids[f];
  });

  // arrows: ends name objects of the list by ref
  records.forEach((rec, i) => {
    if (rec.type !== CoreTypes.connector) return;
    const path = rec.props['path'];
    if (!Array.isArray(path) || path.length < 4 || path.length % 2 || !path.every((v) => typeof v === 'number' && Number.isFinite(v))) {
      throw invalid(`arrow ${objects[i].ref ?? i}: props.path needs at least two points`);
    }
    const end = (key: 'start' | 'end', px: number, py: number): ConnectorEnd => {
      const raw = (rec.props[key] ?? {}) as Record<string, unknown>;
      const { ref, ...rest } = raw;
      const out = { ...rest } as unknown as ConnectorEnd;
      if (!finitePoint(out.point)) out.point = { x: rec.x + px, y: rec.y + py };
      delete out.objectId;
      if (typeof ref === 'string') {
        const t = refs.get(ref);
        if (t === undefined) throw new RpcError(ErrorCodes.notFound, `arrow end ${ref} is not in the list`);
        if (t === i) throw invalid('an arrow cannot be glued to itself');
        out.objectId = ids[t];
      }
      return out;
    };
    const n = path.length;
    rec.props = { ...rec.props, start: end('start', path[0], path[1]), end: end('end', path[n - 2], path[n - 1]) };
  });

  const doc = new CanvasDocument(canvasId);
  try {
    doc.transact((tx) => {
      for (const rec of records) tx.create(rec);
      // group boxes from their members, inner groups first; a group lies just above its members
      const depth = (i: number) => {
        let d = 0;
        for (let cur = parentOf.get(i); cur !== undefined; cur = parentOf.get(cur)) d++;
        return d;
      };
      const groups = objects.map((o, i) => i).filter((i) => objects[i].type === CoreTypes.group);
      groups.sort((a, b) => depth(b) - depth(a));
      for (const g of groups) {
        const id = ids[g];
        if (!doc.has(id)) continue;
        const kids = doc.childrenOf(id).map((k) => doc.get(k)!);
        if (!kids.length) {
          tx.delete(id);
          continue;
        }
        commands.updateGroupBounds(tx, id);
        const top = kids.reduce((z, k) => (k.z > z ? k.z : z), kids[0].z);
        if (doc.get(id)!.z < top) tx.update(id, { z: top });
      }
    });
  } catch (e) {
    if (e instanceof RpcError) throw e;
    if (e instanceof CoordinateLimitError) throw new RpcError('E_COORD_LIMIT', e.message);
    throw new RpcError(ErrorCodes.internal, e instanceof Error ? e.message : String(e));
  }
  return [...doc.allObjects()];
}
